import { useState } from "react";

const API_BASE =
  window.__RUNTIME_CONFIG__?.apiBase ||
  process.env.REACT_APP_API_BASE ||
  "http://127.0.0.1:5000";

// Same palette/layout as the candidate sign-in page (ApplicantPortal.jsx) so
// the two logins feel like one product rather than two different apps.
const BRAND = {
  primary: "#9c27b0",
  primaryDark: "#6a0080",
  text: "#1a1a1a",
  textLight: "#666",
  border: "#e0d4e8",
  bg: "#faf5fc",
};

const inp = {
  padding: "10px 12px",
  border: `1px solid ${BRAND.border}`,
  borderRadius: 8,
  fontSize: 14,
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
  fontFamily: "inherit",
};

const lbl = {
  display: "flex",
  flexDirection: "column",
  fontSize: 13,
  color: BRAND.textLight,
  gap: 5,
  marginBottom: 12,
};

const btnP = (disabled) => ({
  padding: "12px 24px",
  background: disabled ? "#ccc" : BRAND.primary,
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: disabled ? "not-allowed" : "pointer",
  fontWeight: 700,
  fontSize: 15,
  width: "100%",
  fontFamily: "inherit",
});

function extractErrorMessage(payload, fallback) {
  const detail = payload?.detail ?? payload?.error ?? payload?.message;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === "string") return first;
    if (first && typeof first === "object") {
      if (typeof first.msg === "string") return first.msg;
      if (typeof first.message === "string") return first.message;
    }
  }
  if (detail && typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.message === "string") return detail.message;
    return JSON.stringify(detail);
  }
  return fallback;
}

function fromCaughtError(err, fallback) {
  if (err instanceof Error && err.message && err.message !== "[object Object]") return err.message;
  if (typeof err === "string" && err.trim()) return err;
  if (err && typeof err === "object") return extractErrorMessage(err.response?.data || err, fallback);
  return fallback;
}

function Login({ onLogin, onLoginSuccess }) {
    const submitLogin = async (event) => {
      event.preventDefault();
      setError("");
      setSuccess("");
      try {
        setSubmitting(true);
        const response = await fetch(`${API_BASE}/api/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: loginForm.email.trim(),
            password: loginForm.password,
          }),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(extractErrorMessage(data, "Login failed"));
        }
        localStorage.setItem("portalToken", data.access_token);
        const userData = {
          name: data.user_name,
          email: data.user_email,
          role: data.role,
        };
        localStorage.setItem("portalUser", JSON.stringify(userData));
        const cb = onLogin || onLoginSuccess;
        if (cb) cb(userData);
      } catch (submitError) {
        setError(fromCaughtError(submitError, "Login failed"));
      } finally {
        setSubmitting(false);
      }
    };
  const [mode, setMode] = useState("login");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [showForgot, setShowForgot] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotCode, setForgotCode] = useState("");
  const [forgotPassword, setForgotPassword] = useState("");
  const [forgotConfirm, setForgotConfirm] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleLoginChange = (event) => {
    const { name, value } = event.target;
    setLoginForm((prev) => ({ ...prev, [name]: value }));
  };



  const submitForgotPasswordRequest = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: forgotEmail.trim(),
          portal: "employer_admin",
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(extractErrorMessage(data, "Could not send reset code"));
      }
      setForgotStep(2);
      setSuccess(data.message || "If your account exists, a reset code has been sent.");
    } catch (submitError) {
      setError(fromCaughtError(submitError, "Could not send reset code"));
    } finally {
      setSubmitting(false);
    }
  };

  const submitPasswordReset = async (event) => {
    event.preventDefault();
    setSuccess("");
    if (forgotPassword !== forgotConfirm) {
      setError("New password and confirmation do not match");
      return;
    }
    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: forgotCode.trim(),
          new_password: forgotPassword,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(extractErrorMessage(data, "Password reset failed"));
      }
      setSuccess("Password reset successful. Sign in with your new password.");
      setMode("login");
      setLoginForm({ email: forgotEmail.trim(), password: "" });
      setForgotStep(1);
      setForgotEmail(""); setForgotCode(""); setForgotPassword(""); setForgotConfirm("");
    } catch (submitError) {
      setError(fromCaughtError(submitError, "Password reset failed"));
    } finally {
      setSubmitting(false);
    }
  };
  const handleSendReset = async () => {
    setForgotError(""); setForgotSuccess("");
    if (!forgotEmail.trim()) { setForgotError("Email is required."); return; }
    try {
      setForgotLoading(true);
      const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail.trim(), portal: "employer_admin" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not send reset code");
      setForgotSuccess("Reset code sent! Check your email.");
      setForgotStep(2);
    } catch (err) {
      setForgotError(err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setForgotError(""); setForgotSuccess("");
    if (!forgotCode.trim()) { setForgotError("Reset code is required."); return; }
    if (forgotPassword.length < 8) { setForgotError("Password must be at least 8 characters."); return; }
    if (forgotPassword !== forgotConfirm) { setForgotError("Passwords do not match."); return; }
    try {
      setForgotLoading(true);
      const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: forgotCode.trim(), new_password: forgotPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Reset failed");
      setForgotSuccess("Password reset! You can now sign in.");
      setShowForgot(false);
      setForgotStep(1);
      setForgotCode(""); setForgotPassword(""); setForgotConfirm("");
    } catch (err) {
      setForgotError(err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: BRAND.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "system-ui, sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: BRAND.primary, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><circle cx="14" cy="14" r="10" stroke="white" strokeWidth="2"/><path d="M9 14L12.5 17.5L19 11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: BRAND.primary, letterSpacing: "-0.5px" }}>guud vision</div>
          <div style={{ fontSize: 12, color: BRAND.textLight, letterSpacing: "0.1em", textTransform: "uppercase" }}>Staff Portal</div>
        </div>

        <div style={{ background: "#fff", borderRadius: 16, padding: 28, boxShadow: "0 4px 20px rgba(156,39,176,0.08)", border: `1px solid ${BRAND.border}` }}>
          <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: BRAND.text }}>
            {mode === "login" ? "Sign in" : "Reset Password"}
          </h2>
          <p style={{ margin: "0 0 20px", fontSize: 13, color: BRAND.textLight }}>
            {mode === "login"
              ? "Employer / admin access only — candidates use the application portal to sign in."
              : (forgotStep === 1 ? "Enter your email to receive a reset code." : "Enter the code from your email and set a new password.")}
          </p>

          {mode === "login" ? (
            <form onSubmit={submitLogin} style={{ display: "grid", gap: 12 }}>
              <label style={lbl}>Email address
                <input type="email" name="email" value={loginForm.email} onChange={handleLoginChange} required style={inp} placeholder="you@guudvision.health" />
              </label>
              <label style={lbl}>Password
                <input type="password" name="password" value={loginForm.password} onChange={handleLoginChange} required style={inp} placeholder="••••••••" />
              </label>
              {error && <div style={{ background: "#ffebee", color: "#b71c1c", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>{error}</div>}
              {success && <div style={{ background: "#f1f8e9", color: "#2e7d32", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>{success}</div>}
              <button type="submit" disabled={submitting} style={btnP(submitting)}>{submitting ? "Signing in…" : "Sign In"}</button>
            </form>
          ) : (
            <>
              {forgotStep === 1 ? (
                <form onSubmit={e => { e.preventDefault(); handleSendReset(); }} style={{ display: "grid", gap: 12 }}>
                  <label style={lbl}>Email address
                    <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required style={inp} />
                  </label>
                  {forgotError && <div style={{ background: "#ffebee", color: "#b71c1c", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>{forgotError}</div>}
                  {forgotSuccess && <div style={{ background: "#f1f8e9", color: "#2e7d32", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>{forgotSuccess}</div>}
                  <button type="submit" disabled={forgotLoading} style={btnP(forgotLoading)}>{forgotLoading ? "Sending…" : "Send Reset Code"}</button>
                </form>
              ) : (
                <form onSubmit={e => { e.preventDefault(); handleResetPassword(); }} style={{ display: "grid", gap: 12 }}>
                  <label style={lbl}>Reset code
                    <input value={forgotCode} onChange={e => setForgotCode(e.target.value)} required style={inp} />
                  </label>
                  <label style={lbl}>New password
                    <input type="password" value={forgotPassword} onChange={e => setForgotPassword(e.target.value)} required minLength={8} style={inp} />
                  </label>
                  <label style={lbl}>Confirm new password
                    <input type="password" value={forgotConfirm} onChange={e => setForgotConfirm(e.target.value)} required minLength={8} style={inp} />
                  </label>
                  {forgotError && <div style={{ background: "#ffebee", color: "#b71c1c", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>{forgotError}</div>}
                  {forgotSuccess && <div style={{ background: "#f1f8e9", color: "#2e7d32", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>{forgotSuccess}</div>}
                  <button type="submit" disabled={forgotLoading} style={btnP(forgotLoading)}>{forgotLoading ? "Resetting…" : "Reset Password"}</button>
                  <button type="button" onClick={() => { setForgotStep(1); setForgotCode(""); setForgotPassword(""); setForgotConfirm(""); setForgotError(""); setForgotSuccess(""); }} style={{ background: "none", border: "none", color: BRAND.primary, fontSize: 13, cursor: "pointer", padding: 0 }}>
                    ← Back
                  </button>
                </form>
              )}
            </>
          )}

          <button
            onClick={() => {
              if (mode === "login") { setMode("forgot"); setShowForgot(true); setForgotStep(1); setForgotError(""); setForgotSuccess(""); }
              else { setMode("login"); setError(""); setSuccess(""); }
            }}
            style={{ marginTop: 12, background: "none", border: "none", color: BRAND.primary, fontSize: 13, cursor: "pointer", width: "100%", textAlign: "center" }}
          >
            {mode === "login" ? "Forgot password?" : "← Back to Sign In"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Login;
